import React from 'react';
import PropTypes from 'prop-types';
import styles from './TruncatedText.module.scss';

/* ────────────────────────────────────────────────────────────
 * TruncatedText — single-line text that ellipsizes when it
 * overflows its container and reveals the full string in a
 * native tooltip, but ONLY when it is actually clipped.
 *
 *   <TruncatedText text={item.description} />
 *   <TruncatedText as="span" className="foo" text={name} />
 *
 * Overflow is measured lazily on hover / focus (scrollWidth >
 * clientWidth) and the `title` attribute is set just-in-time —
 * so there are no per-cell ResizeObservers and short values
 * never get a redundant tooltip. The full string always stays in
 * the DOM, so assistive tech reads it regardless of the visual
 * clip. The tooltip mechanism matches the design system's
 * native-`title` convention (see IconButton).
 * ──────────────────────────────────────────────────────────── */

const TruncatedText = ({ text, as: Tag, className, ...rest }) => {
  const value = text == null ? '' : String(text);

  // Reveal the tooltip only when the text is genuinely clipped.
  // Set imperatively on enter/focus — the browser reads `title`
  // after its own hover delay, which is well after this fires.
  const reveal = (e) => {
    const el = e.currentTarget;
    const clipped = el.scrollWidth - el.clientWidth > 1; // sub-pixel slack
    el.title = clipped ? value : '';
  };

  return (
    <Tag
      className={[styles.truncate, className].filter(Boolean).join(' ')}
      onMouseEnter={reveal}
      onFocus={reveal}
      {...rest}
    >
      {value}
    </Tag>
  );
};

TruncatedText.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // Element/tag to render — defaults to a <span>.
  as: PropTypes.elementType,
  className: PropTypes.string,
};

TruncatedText.defaultProps = {
  text: '',
  as: 'span',
  className: '',
};

export default TruncatedText;
